import { useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { ArrowLeft, Bookmark, Trash2, Upload } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { ManagerOnly } from "@/components/ManagerOnly";

export const Route = createFileRoute("/tools/deck-templates")({
  component: () => (
    <ManagerOnly>
      <DeckTemplatesPage />
    </ManagerOnly>
  ),
});

function DeckTemplatesPage() {
  const navigate = useNavigate();
  const { deckTemplates, deleteDeckTemplate } = useApp();

  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  const handleDelete = (id: string) => {
    if (confirmDeleteId === id) {
      deleteDeckTemplate(id);
      setConfirmDeleteId(null);
    } else {
      setConfirmDeleteId(id);
    }
  };

  const handleLoad = (id: string) => {
    navigate({
      to: "/products/deck-import",
      search: { templateId: id },
    });
  };

  const sorted = [...deckTemplates].sort((a, b) =>
    b.createdAt.localeCompare(a.createdAt),
  );

  return (
    <section className="flex h-full flex-col">
      <div className="border-b border-border pb-3">
        <Link
          to="/tools"
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" /> Tools
        </Link>
      </div>

      <div className="flex-1 space-y-3 overflow-y-auto py-4 pr-2">
        <div className="space-y-1">
          <h1 className="text-xl font-bold text-foreground">Saved decks</h1>
          <p className="text-sm leading-snug text-muted-foreground">
            Reusable deck-list templates. Tap a deck to load it into the
            deck-import screen, or delete it to remove it from this list.
          </p>
        </div>

        {sorted.length === 0 && (
          <div className="flex flex-col items-center gap-2 rounded-md border border-border bg-card p-6 text-center">
            <Bookmark className="h-8 w-8 text-muted-foreground" />
            <div className="text-sm font-bold text-foreground">
              No saved decks yet
            </div>
            <p className="text-sm leading-snug text-muted-foreground">
              Open the Studio, paste a deck list, then tap "Save list as
              template" to create your first saved deck.
            </p>
            <button
              type="button"
              onClick={() => navigate({ to: "/products/deck-import" })}
              className="mt-2 rounded-md bg-primary px-4 py-2 text-xs font-semibold text-primary-foreground hover:opacity-90"
            >
              Open deck import
            </button>
          </div>
        )}

        {sorted.map((tmpl) => {
          const isConfirmDelete = confirmDeleteId === tmpl.id;
          const lineCount = tmpl.pastedDeckList
            .split(/\r?\n/)
            .filter((l) => l.trim().length > 0).length;
          return (
            <div
              key={tmpl.id}
              className={`space-y-1.5 rounded-md border bg-card p-3 ${
                isConfirmDelete ? "border-destructive" : "border-border"
              }`}
            >
              <div className="flex items-start gap-2.5">
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-bold text-foreground">
                    {tmpl.name}
                  </div>
                  <div className="truncate text-[11px] font-medium capitalize text-muted-foreground">
                    {tmpl.format} · {tmpl.intent} · {lineCount} line
                    {lineCount === 1 ? "" : "s"}
                    {tmpl.themeKeyword ? ` · ${tmpl.themeKeyword}` : ""}
                  </div>
                </div>
                <div className="flex items-center gap-1.5">
                  {isConfirmDelete ? (
                    <>
                      <button
                        type="button"
                        onClick={() => setConfirmDeleteId(null)}
                        className="rounded-md bg-muted px-2.5 py-1 text-xs font-semibold text-muted-foreground"
                      >
                        Keep
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(tmpl.id)}
                        className="rounded-md bg-destructive px-2.5 py-1 text-xs font-semibold text-destructive-foreground"
                      >
                        Delete
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        type="button"
                        onClick={() => handleLoad(tmpl.id)}
                        aria-label="Load template"
                        className="rounded-md p-1.5 text-muted-foreground hover:text-foreground"
                      >
                        <Upload className="h-3.5 w-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(tmpl.id)}
                        aria-label="Delete template"
                        className="rounded-md p-1.5 text-muted-foreground hover:text-foreground"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </>
                  )}
                </div>
              </div>
              <p className="line-clamp-4 whitespace-pre-line text-xs leading-snug text-muted-foreground">
                {tmpl.pastedDeckList || "(empty deck list)"}
              </p>
            </div>
          );
        })}
      </div>
    </section>
  );
}
